import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Plus, Trash2, X } from 'lucide-react';
import { generateFieldId, type LeadFormField, type LeadFormFieldOption } from '../../../lib/crm/leadForms';

type Props = {
  field: LeadFormField;
  onClose: () => void;
  onSave: (field: LeadFormField) => void;
};

const shell = {
  fontFamily:
    '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", system-ui, sans-serif',
};

export default function LeadFormFieldSettingsModal({ field, onClose, onSave }: Props) {
  const [draft, setDraft] = useState<LeadFormField>(field);

  useEffect(() => {
    setDraft(field);
  }, [field]);

  const updateOption = (optionId: string, label: string) => {
    setDraft((prev) => ({
      ...prev,
      options: (prev.options || []).map((o) => (o.id === optionId ? { ...o, label } : o)),
    }));
  };

  const addOption = () => {
    const next: LeadFormFieldOption = {
      id: generateFieldId(`option_${Date.now()}`),
      label: `Option ${(draft.options?.length || 0) + 1}`,
    };
    setDraft((prev) => ({ ...prev, options: [...(prev.options || []), next] }));
  };

  const removeOption = (optionId: string) => {
    setDraft((prev) => ({
      ...prev,
      options: (prev.options || []).filter((o) => o.id !== optionId),
    }));
  };

  const handleTypeChange = (newType: string) => {
    setDraft((prev) => {
      if (newType === 'checkbox') {
        return {
          ...prev,
          type: newType,
          placeholder: undefined,
          options:
            prev.options && prev.options.length > 0
              ? prev.options
              : [
                  { id: generateFieldId('option_1'), label: 'Option 1' },
                  { id: generateFieldId('option_2'), label: 'Option 2' },
                ],
        };
      }
      if (prev.type === 'checkbox' && newType !== 'checkbox') {
        return {
          ...prev,
          type: newType,
          options: undefined,
          placeholder: prev.placeholder || `Enter ${prev.label.toLowerCase()}...`,
        };
      }
      return { ...prev, type: newType };
    });
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[999999] flex items-end sm:items-center justify-center p-2 sm:p-4 bg-black/50 backdrop-blur-sm"
      onClick={onClose}
      style={shell}
    >
      <div
        className="w-full max-w-md bg-white rounded-t-[16px] sm:rounded-[16px] shadow-xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 sticky top-0 bg-white z-10">
          <h3 className="text-[16px] font-semibold text-gray-900" style={{ letterSpacing: '-0.01em' }}>
            Field settings
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-[10px] active:bg-gray-100 touch-manipulation"
            style={{ WebkitTapHighlightColor: 'transparent' }}
            aria-label="Close"
          >
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        <div className="p-4 space-y-4">
          <div>
            <label className="block text-[12px] font-medium text-gray-500 mb-1.5">Label</label>
            <input
              type="text"
              value={draft.label}
              onChange={(e) => setDraft((p) => ({ ...p, label: e.target.value }))}
              className="w-full px-3 py-2.5 border border-gray-200 rounded-[10px] text-[15px] focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              style={{ minHeight: 44 }}
            />
          </div>

          {draft.isCustom && draft.type !== 'checkbox' ? (
            <div>
              <label className="block text-[12px] font-medium text-gray-500 mb-1.5">Placeholder</label>
              <input
                type="text"
                value={draft.placeholder || ''}
                onChange={(e) => setDraft((p) => ({ ...p, placeholder: e.target.value }))}
                className="w-full px-3 py-2.5 border border-gray-200 rounded-[10px] text-[15px] focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                style={{ minHeight: 44 }}
              />
            </div>
          ) : null}

          {draft.isCustom || draft.type === 'checkbox' ? (
            <div>
              <label className="block text-[12px] font-medium text-gray-500 mb-1.5">Type</label>
              <select
                value={draft.type}
                onChange={(e) => handleTypeChange(e.target.value)}
                className="w-full px-3 py-2.5 border border-gray-200 rounded-[10px] text-[15px] focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                style={{ minHeight: 44 }}
              >
                <option value="text">Text</option>
                <option value="textarea">Long text</option>
                <option value="email">Email</option>
                <option value="tel">Phone</option>
                <option value="number">Number</option>
                <option value="url">URL</option>
                <option value="date">Date</option>
                <option value="checkbox">Checkbox</option>
              </select>
            </div>
          ) : null}

          {draft.type === 'checkbox' ? (
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-[12px] font-medium text-gray-500">Options</label>
                <button
                  type="button"
                  onClick={addOption}
                  className="inline-flex items-center gap-1 text-[13px] font-semibold text-blue-600 touch-manipulation"
                  style={{ WebkitTapHighlightColor: 'transparent' }}
                >
                  <Plus className="h-3.5 w-3.5" />
                  Add
                </button>
              </div>
              <div className="space-y-2">
                {(draft.options || []).map((opt) => (
                  <div key={opt.id} className="flex items-center gap-2">
                    <input
                      type="text"
                      value={opt.label}
                      onChange={(e) => updateOption(opt.id, e.target.value)}
                      className="flex-1 px-3 py-2 border border-gray-200 rounded-[10px] text-[14px]"
                      style={{ minHeight: 40 }}
                    />
                    <button
                      type="button"
                      onClick={() => removeOption(opt.id)}
                      className="p-2 text-red-500 active:bg-red-50 rounded-[8px] touch-manipulation"
                      style={{ WebkitTapHighlightColor: 'transparent' }}
                      aria-label="Remove option"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          ) : null}

          {draft.type !== 'customer_info' ? (
            <label className="flex items-center gap-3 py-1 touch-manipulation">
              <input
                type="checkbox"
                checked={draft.mandatory}
                onChange={(e) => setDraft((p) => ({ ...p, mandatory: e.target.checked }))}
                className="h-4 w-4 rounded border-gray-300 text-blue-600"
              />
              <span className="text-[14px] text-gray-800">Required field</span>
            </label>
          ) : (
            <p className="text-[13px] text-gray-500">Customer info fields are always required.</p>
          )}
        </div>

        <div className="p-4 border-t border-gray-100 flex gap-2">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 px-4 py-2.5 rounded-[10px] border border-gray-200 text-[14px] font-semibold text-gray-700 touch-manipulation"
            style={{ minHeight: 44, WebkitTapHighlightColor: 'transparent' }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => onSave(draft)}
            className="flex-1 px-4 py-2.5 rounded-[10px] bg-blue-600 text-white text-[14px] font-semibold touch-manipulation"
            style={{ minHeight: 44, WebkitTapHighlightColor: 'transparent' }}
          >
            Done
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
